import type { CostBasisMethod } from '../api/taxLots';

export type TaxJurisdiction = 'US' | 'UK' | 'AU' | 'EU_DE' | 'EU_FR' | 'EU_OTHER';
export type TaxNextStep = 'review-lots' | 'harvest' | 'export';
export type TaxNextStepTab = 'overview' | 'harvesting' | 'export';
export type TaxNextStepFocus =
  | 'method-comparison'
  | 'lot-ledger'
  | 'harvest-summary'
  | 'harvest-table'
  | 'export-proof'
  | 'export-cards';

export interface TaxNextStepHandoffContext {
  handoff: 'tax-next-step';
  source: 'tax-overview';
  step: TaxNextStep;
  tab: TaxNextStepTab;
  focus: TaxNextStepFocus;
  year?: number;
  method: CostBasisMethod;
  currency: string;
  jurisdiction: TaxJurisdiction;
  proofLabel: string;
  proofCount?: number;
  proofValue?: string;
}

const VALID_TABS: TaxNextStepTab[] = ['overview', 'harvesting', 'export'];
const VALID_STEPS: TaxNextStep[] = ['review-lots', 'harvest', 'export'];
const VALID_FOCUSES: TaxNextStepFocus[] = [
  'method-comparison',
  'lot-ledger',
  'harvest-summary',
  'harvest-table',
  'export-proof',
  'export-cards',
];
const VALID_METHODS: CostBasisMethod[] = ['fifo', 'lifo', 'hifo'];
const VALID_JURISDICTIONS: TaxJurisdiction[] = ['US', 'UK', 'AU', 'EU_DE', 'EU_FR', 'EU_OTHER'];

function setIfPresent(
  params: URLSearchParams,
  key: string,
  value: string | number | null | undefined,
) {
  if (value === null || value === undefined || value === '') {
    return;
  }

  params.set(key, String(value));
}

export function getTaxNextStepPath(context: TaxNextStepHandoffContext): string {
  const params = new URLSearchParams();
  params.set('tab', context.tab);
  params.set('handoff', context.handoff);
  params.set('source', context.source);
  params.set('step', context.step);
  params.set('focus', context.focus);
  params.set('method', context.method);
  params.set('currency', context.currency);
  params.set('jurisdiction', context.jurisdiction);
  params.set('proofLabel', context.proofLabel);
  setIfPresent(params, 'year', context.year);
  setIfPresent(params, 'proofCount', context.proofCount);
  setIfPresent(params, 'proofValue', context.proofValue);
  return `/tax-optimisation?${params.toString()}`;
}

export function readTaxNextStepHandoff(searchParams: URLSearchParams): TaxNextStepHandoffContext | null {
  if (searchParams.get('handoff') !== 'tax-next-step') {
    return null;
  }

  const source = searchParams.get('source');
  const step = searchParams.get('step');
  const tab = searchParams.get('tab');
  const focus = searchParams.get('focus');
  const method = searchParams.get('method');
  const currency = searchParams.get('currency');
  const jurisdiction = searchParams.get('jurisdiction');
  const proofLabel = searchParams.get('proofLabel');

  if (
    source !== 'tax-overview' ||
    !VALID_STEPS.includes(step as TaxNextStep) ||
    !VALID_TABS.includes(tab as TaxNextStepTab) ||
    !VALID_FOCUSES.includes(focus as TaxNextStepFocus) ||
    !VALID_METHODS.includes(method as CostBasisMethod) ||
    !currency ||
    !VALID_JURISDICTIONS.includes(jurisdiction as TaxJurisdiction) ||
    !proofLabel
  ) {
    return null;
  }

  const rawYear = searchParams.get('year');
  const year = rawYear === null ? undefined : Number(rawYear);
  const rawProofCount = searchParams.get('proofCount');
  const proofCount = rawProofCount === null ? undefined : Number(rawProofCount);

  return {
    handoff: 'tax-next-step',
    source: 'tax-overview',
    step: step as TaxNextStep,
    tab: tab as TaxNextStepTab,
    focus: focus as TaxNextStepFocus,
    year: year !== undefined && Number.isFinite(year) ? year : undefined,
    method: method as CostBasisMethod,
    currency,
    jurisdiction: jurisdiction as TaxJurisdiction,
    proofLabel,
    proofCount: proofCount !== undefined && Number.isFinite(proofCount) ? proofCount : undefined,
    proofValue: searchParams.get('proofValue') ?? undefined,
  };
}

export function getTaxNextStepLabel(step: TaxNextStep): string {
  if (step === 'review-lots') return 'Review lots';
  if (step === 'harvest') return 'Harvest losses';
  return 'Export and file';
}

export function getTaxNextStepFocusLabel(focus: TaxNextStepFocus): string {
  switch (focus) {
    case 'method-comparison':
      return 'Method comparison';
    case 'lot-ledger':
      return 'Lot ledger';
    case 'harvest-summary':
      return 'Harvest summary';
    case 'harvest-table':
      return 'Harvest table';
    case 'export-proof':
      return 'Export proof';
    default:
      return 'Export cards';
  }
}
